import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Archive, ArchiveX, Package, FileText, Clock, Calendar } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const statusConfig = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  approved: { label: "Approved", color: "bg-blue-100 text-blue-800 border-blue-200" },
  dispatched: { label: "Dispatched", color: "bg-green-100 text-green-800 border-green-200" },
  invoiced: { label: "Invoicing", color: "bg-purple-100 text-purple-800 border-purple-200" },
  canceled: { label: "Canceled", color: "bg-red-100 text-red-800 border-red-200" },
};

const actionConfig = {
  login: { label: "Login", color: "bg-blue-100 text-blue-800" },
  create_order: { label: "Create Order", color: "bg-green-100 text-green-800" },
  edit_order: { label: "Edit Order", color: "bg-yellow-100 text-yellow-800" },
  cancel_order: { label: "Cancel Order", color: "bg-red-100 text-red-800" },
};

export default function ArchivedTab({ onUnarchive }) {
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [userOrders, setUserOrders] = useState([]);
  const [userLogs, setUserLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchArchivedUsers();
  }, []);

  const fetchArchivedUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/archived-users`, {
        headers: getAuthHeader()
      });
      setArchivedUsers(response.data);
    } catch (error) {
      toast.error("Failed to load archived users");
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}/archive?archive=false`, 
        {},
        { headers: getAuthHeader() }
      );
      toast.success("User unarchived successfully");
      fetchArchivedUsers();
      if (onUnarchive) onUnarchive();
    } catch (error) {
      toast.error("Failed to unarchive user");
    }
  };

  const viewUserDetails = async (user) => {
    setSelectedUser(user);
    setShowDetailsDialog(true);
    
    try {
      // Fetch user's orders
      const ordersRes = await axios.get(`${API}/users/${user.id}/orders`, {
        headers: getAuthHeader()
      });
      setUserOrders(ordersRes.data.orders || []);
      
      // Fetch user's audit logs
      const logsRes = await axios.get(`${API}/audit-logs?hotel_name=${encodeURIComponent(user.hotel_name)}`, {
        headers: getAuthHeader()
      });
      // Filter logs by user_id
      const filteredLogs = logsRes.data.logs.filter(log => log.user_id === user.id);
      setUserLogs(filteredLogs);
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Archive className="w-5 h-5" />
          Archived Users
        </CardTitle>
        <p className="text-sm text-gray-500 mt-1">
          View archived users and their historical data
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading archived users...</div>
        ) : archivedUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No archived users</div>
        ) : (
          <div className="space-y-3">
            {archivedUsers.map((user) => (
              <div
                key={user.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-lg">{user.hotel_name}</h3>
                    <Badge variant="outline" className="bg-gray-100">
                      @{user.username}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {user.order_count} orders
                    </span>
                    {user.last_activity && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Last activity: {formatTimestamp(user.last_activity.timestamp)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => viewUserDetails(user)}
                    variant="outline"
                    size="sm"
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  >
                    <FileText className="w-3 h-3 mr-1" />
                    View Details
                  </Button>
                  <Button
                    onClick={() => handleUnarchive(user.id)}
                    variant="outline"
                    size="sm"
                    className="text-green-600 border-green-300 hover:bg-green-50"
                  >
                    <ArchiveX className="w-3 h-3 mr-1" />
                    Unarchive
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* User Details Dialog */}
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Archive className="w-5 h-5" />
                Archived User: {selectedUser?.hotel_name}
              </DialogTitle>
              <DialogDescription>
                Historical orders and activity logs for @{selectedUser?.username}
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="orders" className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="orders">
                  <Package className="w-4 h-4 mr-2" />
                  Orders ({userOrders.length})
                </TabsTrigger>
                <TabsTrigger value="logs">
                  <FileText className="w-4 h-4 mr-2" />
                  Activity Logs ({userLogs.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="orders" className="space-y-3 mt-4">
                {userOrders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No orders found</div>
                ) : (
                  userOrders.map((order) => (
                    <div key={order.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold">Order #{order.order_number}</h4>
                          <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            Delivery: {new Date(order.delivery_date).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge className={statusConfig[order.status]?.color || "bg-gray-100"}>
                          {statusConfig[order.status]?.label || order.status}
                        </Badge>
                      </div>
                      <div className="space-y-1 mb-3">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="text-sm flex justify-between">
                            <span>{item.product_name} × {item.quantity}</span>
                            <span className="font-medium">${(item.quantity * item.price_per_unit).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      {order.status === "invoiced" && (
                        <div className="border-t pt-2 flex justify-between font-semibold">
                          <span>Total (inc. GST)</span>
                          <span className="text-purple-600">${order.total_price.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </TabsContent>

              <TabsContent value="logs" className="space-y-2 mt-4">
                {userLogs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No activity logs found</div>
                ) : (
                  userLogs.map((log) => (
                    <div key={log.id} className="border rounded-lg p-3 bg-gray-50">
                      <div className="flex items-start gap-3">
                        <Badge className={actionConfig[log.action]?.color || "bg-gray-100"}>
                          {actionConfig[log.action]?.label || log.action}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-600">
                            {formatTimestamp(log.timestamp)}
                          </p>
                          {log.order_number && (
                            <p className="text-sm font-medium mt-1">Order: #{log.order_number}</p>
                          )}
                          {log.details && (
                            <p className="text-sm text-gray-700 mt-1">{log.details}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
